import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Table, Button, Alert, Spinner, Card, CardBody } from 'reactstrap';
import Session from '../auth/Session';
import AuthFunctions from '../services/AuthService';
import { useNavigate } from 'react-router-dom';

function SharePointDriverImport() {
    const [files, setFiles] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState(null);
    const [importing, setImporting] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) { navigate('/logout'); return; }
            const isAdmin = await AuthFunctions.getIsAdminAsync();
            if (!isAdmin) { navigate('/accessdenied'); return; }
            loadFiles();
        })();
    }, []);

    const authHeaders = () => {
        const token = Session.getToken();
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    const loadFiles = async () => {
        setLoadingList(true);
        setListError(null);
        try {
            const res = await fetch('api/sharepoint/driver-files', { headers: authHeaders() });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.message || `Request failed (${res.status}).`);
            }
            const data = await res.json();
            setFiles(data);
        } catch (e) {
            setListError(e.message);
        } finally {
            setLoadingList(false);
        }
    };

    const runImport = async (itemId) => {
        setImporting(itemId || 'default');
        setImportResult(null);
        setImportError(null);
        try {
            const url = itemId
                ? `api/sharepoint/import-drivers/${encodeURIComponent(itemId)}`
                : 'api/sharepoint/import-drivers';
            const res = await fetch(url, { method: 'POST', headers: authHeaders() });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.message || `Import failed (${res.status}).`);
            }
            const data = await res.json();
            setImportResult(data);
        } catch (e) {
            setImportError(e.message);
        } finally {
            setImporting(null);
        }
    };

    const xlsxFiles = files.filter(f => (f.name || '').toLowerCase().endsWith('.xlsx'));
    const zipFiles = files.filter(f => (f.name || '').toLowerCase().endsWith('.zip'));
    const otherFiles = files.filter(f => !xlsxFiles.includes(f) && !zipFiles.includes(f));

    return (
        <Container className='container-main'>
            <Row className='row-20'>
                <Col><h3>SharePoint Driver Import</h3></Col>
                <Col xs='auto'>
                    <Button color='secondary' onClick={loadFiles} disabled={loadingList}>
                        {loadingList ? <Spinner size='sm' /> : 'Refresh'}
                    </Button>{' '}
                    <Button color='primary' onClick={() => runImport(null)} disabled={!!importing}>
                        {importing === 'default' ? <Spinner size='sm' /> : 'Import configured workbook'}
                    </Button>
                </Col>
            </Row>

            {listError && <Alert color='danger'>Could not list SharePoint files: {listError}</Alert>}
            {importError && <Alert color='danger'>Import failed: {importError}</Alert>}

            {importResult && (
                <Card className='mb-3'>
                    <CardBody>
                        <h5>Last import — {importResult.sourceFileName}</h5>
                        <p className='mb-1'>{importResult.message}</p>
                        <p className='mb-1'>
                            <strong>Inserted:</strong> {importResult.inserted || 0}{' '}
                            <strong>Updated:</strong> {importResult.updated || 0}{' '}
                            <strong>Skipped:</strong> {importResult.skipped || 0}
                        </p>
                        {importResult.errors && importResult.errors.length > 0 && (
                            <>
                                <strong>Row errors:</strong>
                                <ul>
                                    {importResult.errors.map((e, i) => (
                                        <li key={i}>
                                            Row {e.rowNumber || '?'}{e.driverName ? ` (${e.driverName})` : ''}: {e.reason}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </CardBody>
                </Card>
            )}

            <Row>
                <Col>
                    <h5>Workbooks</h5>
                    <FileTable
                        files={xlsxFiles}
                        onImport={runImport}
                        importing={importing}
                        emptyText='No .xlsx files found in the configured SharePoint folder.'
                    />

                    <h5 className='mt-4'>DocuSign archives (reference only)</h5>
                    <FileTable files={zipFiles} emptyText='No .zip files found.' />

                    {otherFiles.length > 0 && (
                        <>
                            <h5 className='mt-4'>Other files</h5>
                            <FileTable files={otherFiles} />
                        </>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

function FileTable({ files, onImport, importing, emptyText }) {
    if (!files || files.length === 0) {
        return <p className='text-muted'>{emptyText}</p>;
    }
    return (
        <Table bordered hover size='sm'>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Last modified</th>
                    <th>Size (KB)</th>
                    {onImport && <th></th>}
                </tr>
            </thead>
            <tbody>
                {files.map(f => (
                    <tr key={f.itemId}>
                        <td>
                            {f.webUrl
                                ? <a href={f.webUrl} target='_blank' rel='noreferrer'>{f.name}</a>
                                : f.name}
                        </td>
                        <td>{f.lastModified ? new Date(f.lastModified).toLocaleString() : ''}</td>
                        <td>{f.size ? Math.round(f.size / 1024) : ''}</td>
                        {onImport && (
                            <td>
                                <Button
                                    size='sm'
                                    color='primary'
                                    onClick={() => onImport(f.itemId)}
                                    disabled={!!importing}
                                >
                                    {importing === f.itemId ? <Spinner size='sm' /> : 'Import'}
                                </Button>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export default SharePointDriverImport;
